/**
 * WorkCard コンポーネント
 *
 * 作品情報をカード形式で表示。
 * サムネイル画像、タイトル、制作年、ジャンルを表示。
 * ホバー時にFramer Motionアニメーションを適用。
 *
 */

import { motion, useReducedMotion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import type { Work } from '@/types/domain';
import { getLocalizedText } from '@/lib/i18n-helpers';
import { formatDate } from '@/lib/date-formatters';
import { generateSlug } from '@/lib/data-loader';
import { useLanguageStore } from '@/stores/language-store';
import { cn } from '@/lib/cn';
import { getGenreBackgroundClass } from '@/lib/genre-colors';

/**
 * WorkCard コンポーネントのProps
 */
export interface WorkCardProps {
  /** 作品データ */
  work: Work;

  /** クリック時のコールバック（任意） */
  onClick?: (id: string) => void;

  /** コンパクト表示 */
  compact?: boolean;

  /** 戻り先URL（未指定の場合は現在のURLを使用） */
  returnTo?: string;
}

/**
 * WorkCard コンポーネント
 *
 * 使用例:
 * ```tsx
 * <WorkCard work={workData} onClick={(id) => navigate(`/works/${id}`)} />
 * ```
 */
export function WorkCard({
  work,
  onClick,
  compact = false,
  returnTo,
}: WorkCardProps) {
  const { language } = useLanguageStore();
  const location = useLocation();
  const shouldReduceMotion = useReducedMotion();

  const handleClick = () => {
    onClick?.(work.id);
  };

  const slug = generateSlug(work.id);
  const title = getLocalizedText(work.title, language);
  const genre = work.genre;
  const dateCreated = formatDate(work.dateCreated, language);
  const thumbnail = work.image;

  return (
    <motion.article
      whileHover={shouldReduceMotion ? undefined : { scale: 1.03 }}
      transition={{ duration: 0.2 }}
      className="group overflow-hidden rounded-lg border bg-card shadow-sm transition-shadow hover:shadow-md"
      aria-label={title}
    >
      <Link
        to={`/works/${slug}`}
        state={{
          from: returnTo ?? `${location.pathname}${location.search}`,
        }}
        onClick={handleClick}
        className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      >
        {/* サムネイル画像 */}
        <div
          className={cn(
            compact ? 'aspect-square' : 'aspect-video',
            !compact && (thumbnail ? 'bg-black' : 'bg-muted')
          )}
        >
          <img
            src={thumbnail || '/images/noimage.jpg'}
            alt={title}
            className="h-full w-full object-contain transition-transform group-hover:scale-105"
            loading="lazy"
            width="640"
            height="360"
          />
        </div>

        {/* カード情報 */}
        <div className={compact ? 'p-3' : 'p-4'}>
          {/* タイトル */}
          <h3
            className={cn(
              'line-clamp-2 font-semibold text-foreground',
              compact ? 'text-sm' : 'text-lg'
            )}
          >
            {title}
          </h3>

          {/* メタ情報 */}
          <div className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
            <span>{dateCreated}</span>
            {genre && (
              <>
                <span>•</span>
                <span
                  className={cn(
                    'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold',
                    getGenreBackgroundClass(genre, false)
                  )}
                >
                  {genre}
                </span>
              </>
            )}
          </div>
        </div>
      </Link>
    </motion.article>
  );
}
